
import { supabase } from "@/integrations/supabase/client";
import { Tier1Tracking } from "./Tier1Tracking";
import { useAuth } from "@/contexts/AuthContext";

export class ExtensionContentMonitor {
  private static instance: ExtensionContentMonitor;
  private isMonitoring = false;
  private subscription: any = null;

  static getInstance(): ExtensionContentMonitor {
    if (!ExtensionContentMonitor.instance) {
      ExtensionContentMonitor.instance = new ExtensionContentMonitor();
    }
    return ExtensionContentMonitor.instance;
  }

  async startMonitoring(user: any) {
    if (this.isMonitoring || !user) return;

    console.log('[Extension Monitor] Starting to monitor for extension content saves');
    this.isMonitoring = true;

    // Listen for new content items in real-time
    this.subscription = supabase
      .channel('extension-content-monitor')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'content_items',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => this.handleNewContent(payload, user)
      )
      .subscribe();
  }

  private async handleNewContent(payload: any, user: any) {
    const newContent = payload.new;
    console.log('[Extension Monitor] New content detected:', newContent.id);

    // Check if this content was likely saved by the extension
    const isFromExtension = await this.detectExtensionSource(newContent);
    
    if (isFromExtension) {
      console.log('[Extension Monitor] Content identified as extension save:', newContent.id);
      
      // Track the extension save
      await Tier1Tracking.trackContentSaved(
        newContent.id,
        'extension',
        newContent.content_type,
        newContent.url,
        newContent.title,
        newContent.description,
        user
      );
      
      console.log('[Extension Monitor] Extension save tracked successfully');
    }
  }

  private async detectExtensionSource(content: any): Promise<boolean> {
    // Method 1: Check for extension-specific metadata
    if (content.metadata?.source === 'extension' || content.metadata?.fromExtension) {
      return true;
    }

    // Method 2: Check for extension-specific URL parameters or patterns
    if (content.url && this.hasExtensionIndicators(content.url)) {
      return true;
    }

    // Method 3: Check timing - if content was created very recently and no manual tracking exists
    const recentThreshold = 5000; // 5 seconds
    const contentAge = Date.now() - new Date(content.created_at).getTime();
    
    if (contentAge < recentThreshold) {
      // Check if there's already a manual tracking event for this content
      const { data: existingEvents } = await supabase
        .from('user_activity_events')
        .select('id')
        .eq('event_category', 'content_saving')
        .eq('event_data->>content_id', content.id)
        .eq('event_type', 'content_saved_manual');

      // If no manual tracking exists, it's likely from extension
      return !existingEvents || existingEvents.length === 0;
    }

    return false;
  }

  private hasExtensionIndicators(url: string): boolean {
    // Check for extension-specific URL patterns
    const extensionIndicators = [
      'ext=true',
      'source=extension',
      'clipper=true',
      'extension://',
      'chrome-extension://'
    ];

    return extensionIndicators.some(indicator => 
      url.includes(indicator) || document.referrer.includes(indicator)
    );
  }

  stopMonitoring() {
    if (this.subscription) {
      console.log('[Extension Monitor] Stopping extension content monitoring');
      supabase.removeChannel(this.subscription);
      this.subscription = null;
    }
    this.isMonitoring = false;
  }
}
